import { useState } from 'react';
import { Dice5, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { Input } from './input';
import { toast } from './toast';

const ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';

function generate(len = 16): string {
  const buf = new Uint32Array(len);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < len; i++) out += ALPHABET[buf[i] % ALPHABET.length];
  return out;
}

export function PasswordField({
  value,
  onChange,
  autoFocus,
  minLength = 10,
  placeholder = '至少 10 位',
}: {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  minLength?: number;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  function roll() {
    const pw = generate(16);
    onChange(pw);
    setShow(true);
  }

  async function copy() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('已复制到剪贴板');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('复制失败', '请手动选中复制');
    }
  }

  return (
    <div className="flex items-stretch gap-1.5">
      <div className="relative flex-1">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={minLength}
          autoFocus={autoFocus}
          placeholder={placeholder}
          required
          className="pr-10 font-mono"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:bg-surface-variant hover:text-neutral-700"
          tabIndex={-1}
          aria-label={show ? '隐藏密码' : '显示密码'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <button
        type="button"
        onClick={roll}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-outline-variant text-neutral-600 hover:bg-surface-variant"
        title="生成 16 位强密码"
        aria-label="生成强密码"
      >
        <Dice5 className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={copy}
        disabled={!value}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-outline-variant text-neutral-600 hover:bg-surface-variant disabled:cursor-not-allowed disabled:opacity-50"
        title="复制密码"
        aria-label="复制密码"
      >
        {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
